import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useSecureData } from '../hooks/useSecureData';
import secureApiClient from '../utils/secureApiClient';

const ALLOWED_ACCEPT = 'application/pdf,image/jpeg,image/png';

const CATEGORY_LABELS = {
  'intake-form': 'Intake Form',
  'treatment-plan': 'Treatment Plan',
  'supporting-document': 'Supporting Document',
};

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (ts) => {
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ClientDocuments = ({ clinicalId }) => {
  const { canPerform } = useSecureData();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('supporting-document');
  const [uploading, setUploading] = useState(false);
  const [replacingId, setReplacingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceTargetDocId = useRef(null);

  useEffect(() => {
    if (!clinicalId) { setDocuments([]); setLoading(false); return; }
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'clinicalRecords', clinicalId, 'documents'), orderBy('uploadedAt', 'desc')),
      snap => {
        setDocuments(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => !d.deleted));
        setLoading(false);
      },
      () => { setDocuments([]); setLoading(false); }
    );
    return () => unsub();
  }, [clinicalId]);

  const handleUpload = async (file) => {
    setError('');
    setUploading(true);
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.uploadClientDocument,
        {
          method: 'POST',
          headers: {
            'Content-Type': file.type,
            'X-Clinical-Id': clinicalId,
            'X-File-Name': encodeURIComponent(file.name),
            'X-Doc-Category': category,
          },
          body: file,
        }
      );
    } catch (err) {
      setError(err.message || 'Failed to upload document.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleReplace = async (file) => {
    const docId = replaceTargetDocId.current;
    if (!docId) return;
    setError('');
    setReplacingId(docId);
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.replaceClientDocument,
        {
          method: 'POST',
          headers: {
            'Content-Type': file.type,
            'X-Clinical-Id': clinicalId,
            'X-Doc-Id': docId,
            'X-File-Name': encodeURIComponent(file.name),
          },
          body: file,
        }
      );
    } catch (err) {
      setError(err.message || 'Failed to replace document.');
    } finally {
      setReplacingId(null);
      replaceTargetDocId.current = null;
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  };

  const openDocument = async (docId, action) => {
    setError('');
    try {
      const data = await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.getClientDocumentUrl,
        { method: 'POST', body: JSON.stringify({ clinicalId, docId, action }) }
      );
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message || 'Failed to open document.');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remove this document from the client\'s file?')) return;
    setError('');
    setDeletingId(docId);
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.deleteClientDocument,
        { method: 'POST', body: JSON.stringify({ clinicalId, docId }) }
      );
    } catch (err) {
      setError(err.message || 'Failed to delete document.');
    } finally {
      setDeletingId(null);
    }
  };

  const canView = canPerform('view_documents');
  const canUpload = canPerform('upload_documents');
  const canDelete = canPerform('delete_documents');

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Documents</h3>
        {canUpload && (
          <div className="flex items-center gap-2">
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="intake-form">Intake Form</option>
              <option value="treatment-plan">Treatment Plan</option>
              <option value="supporting-document">Supporting Document</option>
            </select>
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_ACCEPT}
              className="hidden"
              onChange={e => e.target.files[0] && handleUpload(e.target.files[0])}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : '+ Upload'}
            </button>
          </div>
        )}
      </div>

      {/* Hidden input used for the Replace action, shared across rows */}
      <input
        ref={replaceInputRef}
        type="file"
        accept={ALLOWED_ACCEPT}
        className="hidden"
        onChange={e => e.target.files[0] && handleReplace(e.target.files[0])}
      />

      {error && (
        <div className="mb-4 px-4 py-2 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-500 py-8 text-sm">Loading documents…</div>
      ) : documents.length === 0 ? (
        <div className="text-center text-gray-500 py-8 text-sm">No documents on file</div>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => (
            <div key={doc.id} className="flex items-center justify-between border rounded-lg p-3">
              <div className="min-w-0">
                <div className="font-medium text-gray-800 truncate">{doc.fileName}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {CATEGORY_LABELS[doc.category] || doc.category} · {formatSize(doc.size)} · {formatDate(doc.uploadedAt)}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                {canView && (
                  <>
                    <button onClick={() => openDocument(doc.id, 'view')}
                      className="text-xs px-2.5 py-1.5 rounded-lg text-brand-600 hover:bg-brand-50 font-medium">
                      View
                    </button>
                    <button onClick={() => openDocument(doc.id, 'download')}
                      className="text-xs px-2.5 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 font-medium">
                      Download
                    </button>
                  </>
                )}
                {canUpload && (
                  <button
                    onClick={() => { replaceTargetDocId.current = doc.id; replaceInputRef.current?.click(); }}
                    disabled={replacingId === doc.id}
                    className="text-xs px-2.5 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 font-medium disabled:opacity-50"
                  >
                    {replacingId === doc.id ? 'Replacing…' : 'Replace'}
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => handleDelete(doc.id)}
                    disabled={deletingId === doc.id}
                    className="text-xs px-2.5 py-1.5 rounded-lg text-red-600 hover:bg-red-50 font-medium disabled:opacity-50"
                  >
                    {deletingId === doc.id ? 'Removing…' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientDocuments;
